import { Box, Typography, Paper, Chip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Incident status type
 */
export type IncidentStatus = 'ONGOING' | 'RESOLVED';

/**
 * Incident data structure
 */
export interface IncidentData {
  id: string;
  monitorId: string;
  monitorDisplayName: string;
  status: IncidentStatus;
  cause: string | null;
  startedAt: Date | string;
  resolvedAt: Date | string | null;
  durationMs: number | null;
  isManual: boolean;
  title: string | null;
  message: string | null;
}

interface IncidentCardProps {
  incident: IncidentData;
  primaryColor?: string;
}

/**
 * Status configuration for incidents
 */
const statusConfig: Record<
  IncidentStatus,
  {
    label: string;
    color: string;
    backgroundColor: string;
    borderColor: string;
    icon: React.ReactElement;
  }
> = {
  ONGOING: {
    label: 'Ongoing',
    color: '#EF4444',
    backgroundColor: 'rgba(239, 68, 68, 0.05)',
    borderColor: 'rgba(239, 68, 68, 0.3)',
    icon: <WarningIcon sx={{ fontSize: 18 }} />,
  },
  RESOLVED: {
    label: 'Resolved',
    color: '#10B981',
    backgroundColor: 'rgba(16, 185, 129, 0.05)',
    borderColor: 'rgba(16, 185, 129, 0.3)',
    icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
  },
};

/**
 * Format duration in a human-readable format
 */
function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date | string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  return 'Just now';
}

/**
 * IncidentCard component
 * Displays incident information with status, timestamp, and details
 */
export function IncidentCard({ incident }: IncidentCardProps) {
  const config = statusConfig[incident.status];
  const isOngoing = incident.status === 'ONGOING';

  // Calculate current duration for ongoing incidents
  const currentDuration = isOngoing
    ? Date.now() - new Date(incident.startedAt).getTime()
    : incident.durationMs;

  return (
    <Paper
      sx={{
        p: 2,
        border: 1,
        borderColor: config.borderColor,
        backgroundColor: config.backgroundColor,
        borderRadius: 2,
      }}
      elevation={0}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        {/* Status icon */}
        <Box sx={{ color: config.color, mt: 0.25 }}>
          {incident.isManual ? <BuildIcon sx={{ fontSize: 20 }} /> : config.icon}
        </Box>

        {/* Title and status */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {incident.title ||
                (incident.isManual ? 'Scheduled Maintenance' : 'Service Disruption')}
            </Typography>
            <Chip
              label={incident.isManual ? 'Maintenance' : config.label}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: incident.isManual
                  ? 'rgba(245, 158, 11, 0.1)'
                  : config.backgroundColor,
                color: incident.isManual ? '#F59E0B' : config.color,
                border: 1,
                borderColor: incident.isManual ? 'rgba(245, 158, 11, 0.3)' : config.borderColor,
              }}
            />
          </Box>

          {/* Affected monitor */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Affected: {incident.monitorDisplayName}
          </Typography>
        </Box>
      </Box>

      {/* Message/Description */}
      {incident.message && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {incident.message}
          </Typography>
        </Box>
      )}

      {/* Cause (for non-manual incidents) */}
      {!incident.isManual && incident.cause && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Cause: {incident.cause}
          </Typography>
        </Box>
      )}

      {/* Timeline */}
      <Box
        sx={{
          mt: 2,
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        {/* Started */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Started
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {formatDate(incident.startedAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(incident.startedAt)}
          </Typography>
        </Box>

        {/* Resolved */}
        {incident.resolvedAt && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Resolved
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(incident.resolvedAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(incident.resolvedAt)}
            </Typography>
          </Box>
        )}

        {/* Duration */}
        {currentDuration && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {isOngoing ? 'Duration (ongoing)' : 'Duration'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: isOngoing ? config.color : 'text.primary',
              }}
            >
              {formatDuration(currentDuration)}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default IncidentCard;
